import { DataTable, type DataTableColumn } from "@woba/ds-web-next";
import { formatInteger, useIntlLocale } from "@/core/utils/locale";
import { useMemo } from "react";

export type RankingsRow = {
  position: number;
  name: string;
  value: number;
  email?: string;
};

type RankingsDataTableProps = {
  rows: RankingsRow[];
  isLoading: boolean;
  nameHeader: string;
  valueHeader: string;
  /** Coluna extra de e-mail (aba Membros). */
  emailHeader?: string;
  "data-testid"?: string;
};

/**
 * Ranking no DataTable do Workbase DS (Figma 6037:11041): nome + reservas,
 * já ordenado pela posição que vem da API (sem ordenação/paginação local).
 * O contêiner "Table" do Figma tem borda stroke-default e raio medium (16px);
 * o DataTable do DS não desenha essa moldura, então ela fica no wrapper (e a
 * linha da última linha some, para não dobrar com a borda do contêiner).
 */
export function RankingsDataTable({
  rows,
  isLoading,
  nameHeader,
  valueHeader,
  emailHeader,
  "data-testid": testId,
}: RankingsDataTableProps) {
  const locale = useIntlLocale();
  const columns = useMemo<DataTableColumn<RankingsRow>[]>(() => {
    const base: DataTableColumn<RankingsRow>[] = [
      { id: "name", header: nameHeader, accessor: (row) => row.name },
    ];
    if (emailHeader) {
      base.push({
        id: "email",
        header: emailHeader,
        accessor: (row) => row.email ?? "-",
      });
    }
    base.push({
      id: "value",
      header: valueHeader,
      accessor: (row) => formatInteger(row.value, locale),
    });
    return base;
  }, [nameHeader, valueHeader, emailHeader, locale]);

  return (
    <div
      className="overflow-hidden w-full min-w-0 [&_tbody_tr:last-child]:!border-b-0 border border-solid border-[color:var(--woba-color-stroke-default)] rounded-[var(--woba-border-radius-medium)] bg-[var(--woba-color-surface-default)]"
      data-testid={testId}
    >
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => `${row.position}-${row.name}`}
        loading={isLoading}
      />
    </div>
  );
}
